import {
  Button,
  FileInputControlled,
  Modal,
  useMobile,
  addToast,
} from '@octano/global-ui';
import {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Ref,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useActionKitImp } from '../../../components/ozmoit/common/action-kit-imp';
import { useValidations } from '../../../hooks/useValidations';
import { TranslationsKeys } from '../../../locales/translations';
import { downloadFromBlob } from '../../../utils/blob';
import { importAction } from '../actions';
import { StudentWarningsGroupType } from '../types';

export type MassiveImportModalMethods = {
  open: () => void;
  close: () => void;
};

type MassiveImportModalProps = {
  onUpload?: () => void;
  warningGroupType: StudentWarningsGroupType;
  schoolId: number;
};

type UploadFields = {
  file: File | null;
};

const MassiveImportModal = (
  { onUpload, schoolId, warningGroupType }: MassiveImportModalProps,
  ref: Ref<MassiveImportModalMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_SUMMARY);
  const isMobile = useMobile();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { msgValidations } = useValidations();

  const { control, handleSubmit, reset } = useForm<UploadFields>({
    defaultValues: { file: null },
  });

  const { action: handleUpload, loading: uploading } = useActionKitImp(
    importAction,
    {
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`massiveImport.failedUpload`),
          });
        },
      },
    },
  );

  const close = useCallback(() => {
    if (uploading) {
      return;
    }
    setIsOpen(false);
  }, [uploading]);

  const open = useCallback(() => {
    if (uploading) {
      return;
    }
    reset({ file: null });
    setIsOpen(true);
  }, [uploading, reset]);

  const handleConfirm = useCallback(
    async (values: UploadFields) => {
      if (!values?.file || uploading) {
        return;
      }
      const response = await handleUpload(
        values?.file,
        warningGroupType,
        schoolId,
      );

      if (!response.success) {
        return;
      }

      if (response.data) {
        const blob = new Blob([response.data], { type: 'vnd.ms-excel' });
        downloadFromBlob(
          blob,
          `${t(
            `massiveImport.failedTemplateName`,
          )} ${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`,
        );
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`massiveImport.failedUpload`),
        });
        return;
      }

      addToast({
        icon: 'success',
        color: 'success',
        text: t(`massiveImport.successUpload`),
      });

      setIsOpen(false);
      !!onUpload && onUpload();
    },
    [onUpload, handleUpload, uploading, t, schoolId, warningGroupType],
  );

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  return (
    <Modal toggle={close} isOpen={isOpen}>
      <div>
        <h1 className="text-dark text-center fw-600 fs-20">
          {t(`massiveImport.titleModal`)}
        </h1>
        <p className=" text-center fs-16 lh-30 my-3">
          {t(`massiveImport.descriptionModal`)}
        </p>
        <FileInputControlled
          btnText={t(`massiveImport.import`)}
          control={control}
          name="file"
          label={t(`massiveImport.label`)}
          rules={{ required: msgValidations.required }}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        />
        <div
          className="d-flex flex-wrap justify-content-between mt-4"
          style={{ gap: '1rem' }}
        >
          <Button
            text={t(`common:actions.close`)}
            outlined
            style={{ width: isMobile ? '100%' : 197 }}
            disabled={uploading}
            onClick={close}
          />
          <Button
            text={t(`massiveImport.import`)}
            style={{ width: isMobile ? '100%' : 197 }}
            loading={uploading}
            onClick={handleSubmit(handleConfirm)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(MassiveImportModal);
